import { useState } from 'react';
import type { PushSubscribeResult, Tv2mInitResult } from '@apm/tv2m-client-sdk';
import { useT } from '../i18n/LocaleProvider.js';
import type { MessageKey } from '../i18n/ko.js';

export type PushEnabler = () => Promise<PushSubscribeResult>;

/**
 * 알림 권한 안내와 요청 버튼.
 *
 * ── WB-07: 권한 요청은 사용자 제스처 직후에만 ────────────────
 *
 * **`onClick` 안에서만 부른다.** 브라우저는 제스처 밖의 `Notification.requestPermission()`
 * 을 거부하거나 조용히 무시하고, **한 번 거부된 권한은 사이트 설정에서만 되돌릴 수 있어
 * 두 번째 기회가 없다.** 그래서 마운트 효과에서 자동으로 묻지 않는다 — 그 편이 구독률이
 * 높아 보이지만, 맥락 없이 뜬 권한창은 거의 거부되고 그 거부는 영구적이다.
 *
 * 코어가 `requestPermission()` 을 부르지 않는 이유가 이것이다 (`PushAdapter` 주석) —
 * 어떤 호출이 제스처 안에 있는지 아는 것은 버튼을 그린 이 컴포넌트뿐이다.
 *
 * ── WB-08: iOS A2HS 미설치 ───────────────────────────────────
 *
 * iOS 는 홈 화면에 추가되기 전에는 `PushManager` 자체가 없다. 그 상태에서 "알림을 켜
 * 주세요" 를 띄우면 **사용자가 켤 수 있는 스위치가 없다** — 눌러도 아무 일이 일어나지
 * 않는 버튼을 보여 주는 셈이다. 버튼을 감추고 홈 화면에 추가하라고 안내한다.
 *
 * **판정을 기능 검사로 하지 않는다.** `PushAdapter.isSupported()` 가 이미 `false` 를
 * 주지만 **이유를 구분하지 못한다** — 웹 알림이 아예 없는 브라우저와 "설치하면 되는"
 * iOS 는 안내가 서로 다르다. `needsIosInstallForPush()` 가 그 구분을 맡고, 그것이
 * SDK 코어가 아니라 웹 어댑터 계층에 있는 이유도 같다 (UA 판정은 플랫폼 사정이다).
 */
export function NotificationPermission({
  init,
  subscribed,
  needsIosInstall,
  onEnablePush,
}: {
  init: Tv2mInitResult | null;
  /**
   * 서버에 구독이 붙었는가. **`init.pushSubscribed` 를 읽지 않는다** — 그 값은 SDK 가
   * 아는 사실이고, federated 에서 구독을 가진 것은 BLOOM Server 라 SDK 는 모른다. 그대로 읽으면
   * **알림이 켜져 있는데 「알림을 켜 주세요」가 남는다** (`useTv2m` 의 `pushSubscribed`).
   */
  subscribed: boolean;
  needsIosInstall: boolean;
  onEnablePush: PushEnabler;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PushSubscribeResult | null>(null);

  if (init === null) return null;

  // **설치 안내가 구독 배지보다 먼저다.** iOS 에서는 설치 전에 구독이 성립할 수 없으므로
  // 두 조건이 동시에 참일 수 없지만, 순서를 뒤집으면 그 불변식에 기대게 된다.
  if (needsIosInstall) {
    return (
      <div className="card">
        <h2 className="card__title">{t('push.iosTitle')}</h2>
        <p className="card__text">{t('push.iosText')}</p>
        <ol className="steps">
          <li>{t('push.iosStep1')}</li>
          <li>{t('push.iosStep2')}</li>
          <li>{t('push.iosStep3')}</li>
        </ol>
      </div>
    );
  }

  if (subscribed) {
    return (
      <div className="badge-row">
        <span className="badge badge--ok">{t('push.on')}</span>
      </div>
    );
  }

  const enable = () => {
    setBusy(true);
    // **`await` 를 거치지 않고 바로 부른다.** 제스처 컨텍스트는 마이크로태스크를 하나만
    // 건너도 사라지는 브라우저가 있다 — 권한 요청이 그 안에서 일어나야 한다 (WB-07).
    void onEnablePush()
      .then(setResult)
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="card">
      <h2 className="card__title">{t('push.enableTitle')}</h2>
      <p className="card__text">{t('push.enableText')}</p>
      <p className="card__text">{t(describeKey(init, result))}</p>
      <div style={{ marginTop: '0.9rem' }}>
        <button
          type="button"
          className="button"
          data-testid="enable-push"
          onClick={enable}
          disabled={busy}
        >
          {busy ? t('push.requesting') : t('push.enable')}
        </button>
      </div>
    </div>
  );
}

/**
 * 왜 못 붙였는지를 말해 준다. **문구가 아니라 사전 키다** (절대 규칙 24).
 *
 * **`PushSubscribeResult.reason` 이 존재하는 이유가 이 문단이다** (T6 이 `Promise<void>`
 * 원안을 개정한 자리). 안내가 서로 다르다 — 거부는 사이트 설정으로, 키 없음은 서버
 * 설정으로, 지원 없음은 브라우저를 바꾸는 것으로만 풀린다.
 */
function describeKey(init: Tv2mInitResult, result: PushSubscribeResult | null): MessageKey {
  if (result?.subscribed) return 'push.subscribed';

  switch (result?.reason) {
    case 'permission_denied':
      return 'push.denied';
    case 'no_vapid_key':
      return 'push.noVapid';
    case 'unsupported':
      return 'push.unsupported';
    case 'subscribe_failed':
      return 'push.subscribeFailed';
    case 'key_mismatch':
      // 절대 규칙 19 가 말하는 그 침묵이다. 여기서 말하지 않으면 「알림이 안 온다」만 남는다.
      return 'push.keyMismatch';
    case 'wrong_mode':
      return 'push.modeMismatch';
    case 'server_error':
      return 'push.registerFailed';
    default:
      break;
  }

  if (init.pushPermission === 'denied') return 'push.denied';
  return 'push.notAsked';
}
